import { Drawer, Box, IconButton, Avatar, Typography, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UserDetailTabs from './UserDetailTabs';
import { AdminUserDetail } from '../../../types/admin';

interface UserDetailModalProps {
  open: boolean;
  userId: string | null;
  getUserDetail: (userId: string) => AdminUserDetail | null;
  onClose: () => void;
  onBanUser: () => void;
  onUnbanUser: () => void;
  onDeleteUser: () => void;
}

/**
 * UserDetailModal - Drawer-based user detail view
 *
 * Features:
 * - Right-side slide-out drawer (keeps main table visible)
 * - User header with avatar and name
 * - Tabbed content (Overview, Activity, Homes, Relationships, Moderation)
 * - Close button
 * - Loading state while fetching user details
 *
 * Design Decision: Using Drawer instead of Modal to maintain context
 * of the user table while viewing details (best practice from research)
 */
export default function UserDetailModal({
  open,
  userId,
  getUserDetail,
  onClose,
  onBanUser,
  onUnbanUser,
  onDeleteUser,
}: UserDetailModalProps) {
  const user = userId ? getUserDetail(userId) : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90%', md: '70%', lg: '60%' },
          maxWidth: 1000,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {user ? (
          <>
            <Avatar
              src={user.picture}
              alt={user.name}
              sx={{ width: 56, height: 56 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">{user.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </>
        ) : (
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">User Details</Typography>
          </Box>
        )}
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ overflow: 'auto', height: 'calc(100% - 100px)' }}>
        {!user ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ px: 3 }}>
            <UserDetailTabs
              user={user}
              onBanUser={onBanUser}
              onUnbanUser={onUnbanUser}
              onDeleteUser={onDeleteUser}
            />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
